import Phaser from 'phaser';
import { Behavior, BehaviorConfig } from './Behavior';
import { Entity } from '../base/Entity';

export interface PinConfig extends BehaviorConfig {
  /** The entity to pin to */
  target?: Entity;
  /** Image point name on target to pin to (optional, defaults to center) */
  imagePoint?: string;
  /** Whether to copy target's angle */
  pinAngle?: boolean;
  /** Offset X from pin point */
  offsetX?: number;
  /** Offset Y from pin point */
  offsetY?: number;
}

/**
 * PinBehavior - Attaches an entity to another entity
 *
 * Equivalent to Construct 3's "Pin" behavior.
 * Makes the entity follow another entity's position (optionally at an image point).
 *
 * Used by: cannonCannon (pinned to cannonBase)
 *
 * Features:
 * - Follow target entity position
 * - Pin to specific image point on target
 * - Optional angle copying
 * - Offset support
 */
export class PinBehavior extends Behavior {
  private target: Entity | null = null;
  private imagePoint: string | null = null;
  private pinAngle: boolean;
  private offsetX: number;
  private offsetY: number;

  constructor(entity: Entity, config: PinConfig = {}) {
    super(entity, config);

    this.target = config.target ?? null;
    this.imagePoint = config.imagePoint ?? null;
    this.pinAngle = config.pinAngle ?? false;
    this.offsetX = config.offsetX ?? 0;
    this.offsetY = config.offsetY ?? 0;
  }

  /**
   * Pin to a target entity
   * @param target The entity to follow
   * @param imagePoint Optional image point name on target
   * @param pinAngle Whether to copy target's angle
   */
  pinTo(target: Entity, imagePoint?: string, pinAngle: boolean = false): void {
    this.target = target;
    this.imagePoint = imagePoint ?? null;
    this.pinAngle = pinAngle;

    // Listen for target destruction
    target.once('destroy', () => {
      this.target = null;
    });

    // Initial update to position immediately
    this.updatePosition();
  }

  /**
   * Unpin from target
   */
  unpin(): void {
    this.target = null;
    this.imagePoint = null;
  }

  /**
   * Set offset from pin point
   */
  setOffset(x: number, y: number): void {
    this.offsetX = x;
    this.offsetY = y;
  }

  /**
   * Update - follow target position
   */
  override update(_time: number, _delta: number): void {
    if (!this.enabled || !this.target) return;
    this.updatePosition();
  }

  /**
   * Update entity position to follow target
   */
  private updatePosition(): void {
    if (!this.target || !this.target.active) return;

    let targetX: number;
    let targetY: number;

    // Get position from image point or center
    if (this.imagePoint) {
      const point = this.target.getImagePoint(this.imagePoint);
      if (point) {
        targetX = point.x;
        targetY = point.y;
      } else {
        // Fallback to center
        targetX = this.target.x;
        targetY = this.target.y;
      }
    } else {
      targetX = this.target.x;
      targetY = this.target.y;
    }

    // Apply offset (rotated if pinAngle is true)
    if (this.offsetX !== 0 || this.offsetY !== 0) {
      if (this.pinAngle && this.target.angle !== 0) {
        // Rotate offset by target angle
        const rad = Phaser.Math.DegToRad(this.target.angle);
        const cos = Math.cos(rad);
        const sin = Math.sin(rad);
        targetX += this.offsetX * cos - this.offsetY * sin;
        targetY += this.offsetX * sin + this.offsetY * cos;
      } else {
        targetX += this.offsetX;
        targetY += this.offsetY;
      }
    }

    // Set position
    this.entity.setPosition(targetX, targetY);

    // Copy angle if configured
    if (this.pinAngle) {
      this.entity.setAngle(this.target.angle);
    }
  }

  /**
   * Check if currently pinned
   */
  isPinned(): boolean {
    return this.target !== null && this.target.active;
  }

  /**
   * Get current target
   */
  getTarget(): Entity | null {
    return this.target;
  }

  /**
   * Clean up when behavior is destroyed
   */
  override destroy(): void {
    this.target = null;
    super.destroy();
  }
}
